import { ResizeObserver } from "@juggle/resize-observer";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { NavigationLinkData } from "./types";

interface UseResponsiveNavLinksParams {
  links: NavigationLinkData[];
  maxVisibleLinks: number;
}

const MORE_BUTTON_WIDTH = 80; // placeholder width for the more button
const LINK_GAP = 8; // matches gap-2 on the nav list

export const useResponsiveNavLinks = ({
  links,
  maxVisibleLinks,
}: UseResponsiveNavLinksParams) => {
  const containerElementRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [visibleCount, setVisibleCount] = useState(maxVisibleLinks);
  const [reCalculateCount, setReCalculateCount] = useState(0);
  const [prevMaxVisibleLinks, setPrevMaxVisibleLinks] =
    useState(maxVisibleLinks);

  // maxVisibleLinks changes when the viewport crosses a breakpoint (e.g. 6 to 8).
  // When that happens, render up to the new limit so we can measure whether
  // all of them fit, or whether some need to be hidden behind More.
  if (maxVisibleLinks !== prevMaxVisibleLinks) {
    setPrevMaxVisibleLinks(maxVisibleLinks);
    setVisibleCount(maxVisibleLinks);
  }

  const isMountedRef = useRef(false);

  const calculateVisibleLinks = useCallback(() => {
    const container = containerElementRef.current;

    if (!container) {
      return;
    }

    // Already showing a set that fits. Do not snap back up to the cap —
    // that re-renders more links, they overflow, and we loop.
    if (container.scrollWidth <= container.clientWidth) {
      return;
    }

    const linkItems =
      container.querySelectorAll<HTMLElement>("[data-nav-link]");
    const availableWidth = container.clientWidth - MORE_BUTTON_WIDTH;

    let usedWidth = 0;
    let count = 0;

    // Calculate the number of links that fit in the container
    for (const item of Array.from(linkItems)) {
      const gap = count > 0 ? LINK_GAP : 0;
      if (usedWidth + gap + item.offsetWidth > availableWidth) {
        break;
      }

      usedWidth += gap + item.offsetWidth;
      count++;
    }

    setVisibleCount(Math.min(maxVisibleLinks, Math.max(1, count)));
  }, [maxVisibleLinks]);

  // Calculate the visible count on initial render and when the links change
  useLayoutEffect(() => {
    calculateVisibleLinks();
  }, [calculateVisibleLinks, links, reCalculateCount]);

  const handleContainerResize = useCallback(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    setVisibleCount(maxVisibleLinks);
    setReCalculateCount(prev => prev + 1);
  }, [maxVisibleLinks]);

  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerElementRef.current = node;

      // disconnect the observer if it exists before creating a new one
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (!node) {
        isMountedRef.current = false;
        return;
      }

      const observer = new ResizeObserver(handleContainerResize);
      // The nav list is content-sized, so observe the header to catch viewport resizes
      observer.observe(node.closest("nav") ?? node);
      observerRef.current = observer;
    },
    [handleContainerResize],
  );

  const { visibleLinks, hiddenLinks } = useMemo(
    () => ({
      visibleLinks: links.slice(0, visibleCount),
      hiddenLinks: links.slice(visibleCount),
    }),
    [links, visibleCount],
  );

  return { containerRef, visibleLinks, hiddenLinks };
};
